/* Utils */
import {createCacheManagerInstance} from '../../core/cache/cacheUtils';
import {generateRandomGuid} from '../string/stringUtils';

/* Constants */
import {MAX_API_PAGINATION_PAGE_NUMBER_LIMIT, API_NAME, HTTP_AUTHORIZATION_METHOD} from './apiConstants';
import {CLIENT_ID_HEADER, CORRELATION_ID_HEADER, STATSIG_HEADER, STATSIG_COOKIE_PREFIX} from './networkConstants';

function isApiPaginationLimitExceeded(pageNumber) {
  return parseInt(pageNumber) > MAX_API_PAGINATION_PAGE_NUMBER_LIMIT;
}

function getBaseForeignApiNetworkConfig() {
  return {
    baseURL: '',
    withCredentials: process.env.SF_MOBILE_ENV !== 'prod',
  };
}

function getAuthorizationHeaderWithBearer(token) {
  return token && {
    Authorization: `${HTTP_AUTHORIZATION_METHOD.BEARER} ${token}`,
  } || {}
}

function generateCustomHeadersForTracking() {
  return {
    [CLIENT_ID_HEADER]: API_NAME,
    [CORRELATION_ID_HEADER]: generateRandomGuid(),
  };
}

function generateHeadersByCookieValuesFilterByStartsWith(cookies, cookieKeys) {
  const headers = {};
  if(cookies){
    for (let key in cookies) {
      if(cookieKeys.findIndex((cookieKey) => {
        return key.startsWith(cookieKey);
      }, key) >=0){
        headers[key] = cookies[key];
      }
    }
  }
  return headers;
}

function generateHeadersByCookieValuesFilterByEqual(cookies, cookieKeys) {
  const headers = {};
  for (let key in cookies) {
    if(cookieKeys.includes(key)){
      headers[key] = cookies[key];
    }
  }
  return headers;
}

const generateStatsigHeaderByPageType = (cookies, pageType) => {
  const key = `${STATSIG_COOKIE_PREFIX}${pageType}`;
  const value = cookies?.[key];

  if (value) {
    return {
      [STATSIG_HEADER]: value
    }
  }

  return {};
};

function getCookies(rawCookies) {
  const cookies = rawCookies || document.cookie;

  return cookies?.split(';')?.reduce((cookies, cookie) => {
    const parts = cookie?.split('=');

    if (parts?.length === 2) {
      cookies[parts[0]?.trim()] = decodeURIComponent(parts?.[1]);
    } else if (parts.length > 2) {
      const key = parts?.shift()?.trim();
      cookies[key] = decodeURIComponent(parts?.join('='));
    }

    return cookies;
  }, {});
}

function createApiClient(apiManager, networkConfig = {}) {
  const cacheManager = createCacheManagerInstance();

  function getPromise(method, url, paramsArgument, configArgument) {
    let config, params, promise;

    if (configArgument) {
      config = configArgument;
      params = paramsArgument;

      promise = apiManager.api[method](
        url,
        params,
        {
          ...config,
          ...networkConfig,
        },
      );
    } else {
      config = paramsArgument;
      promise = apiManager.api[method](
        url,
        {
          ...config,
          ...networkConfig,
        },
      );
    }

    return promise;
  }

  function makeRequest(method, url, paramsArgument, configArgument, cacheSettings) {
    let promise, isCacheEnabled, cacheResponse;

    isCacheEnabled = cacheSettings && cacheSettings.cacheStatus && cacheSettings.cacheKey;

    if (isCacheEnabled) {
      cacheResponse = cacheManager.get(cacheSettings);

      if (cacheResponse && !cacheResponse.isExpired) {
        //console.log('Came from cache', cacheSettings.cacheKey);
        promise = Promise.resolve(cacheResponse.cacheValue);
      } else {
        //console.log('Not exist cache, request sent', cacheSettings.cacheKey);
        promise = getPromise(method, url, paramsArgument, configArgument)
          // eslint-disable-next-line dot-location
          .then(response => {
            cacheManager.put(cacheSettings, response);
            return response;
          })
          // eslint-disable-next-line dot-location
          .catch(error => {
            if (cacheResponse) {
              //console.log('Came from cache - IN ERROR BLOCK', cacheSettings.cacheKey);
              return Promise.resolve(cacheResponse.cacheValue);
            } else {
              throw error;
            }

          });
      }
    } else {
      //console.log('Doesnt have CacheSettings', url);
      promise = getPromise(method, url, paramsArgument, configArgument);
    }

    return promise;
  }

  return {
    get(url, params, config, cacheSettings) {
      return makeRequest('get', url, params, config, cacheSettings);
    },

    post(url, params, config, cacheSettings) {
      return makeRequest('post', url, params, config, cacheSettings);
    },

    put(url, params, config, cacheSettings) {
      return makeRequest('put', url, params, config, cacheSettings);
    },

    delete(url, config, cacheSettings) {
      return makeRequest('delete', url, config, null, cacheSettings);
    },

    head(url, config, cacheSettings) {
      return makeRequest('head', url, config, null, cacheSettings);
    },

    options(url, config, cacheSettings) {
      return makeRequest('options', url, config, null, cacheSettings);
    },
  };
}

export {
  isApiPaginationLimitExceeded,
  getBaseForeignApiNetworkConfig,
  createApiClient,
  getAuthorizationHeaderWithBearer,
  generateCustomHeadersForTracking,
  generateHeadersByCookieValuesFilterByEqual,
  generateHeadersByCookieValuesFilterByStartsWith,
  generateStatsigHeaderByPageType,
  getCookies,
};
